import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Modal } from '@mui/material';

import { PendingChangesModalBodyWrapperStyled } from './PendingChangesModalStyled';
import { Pill } from 'components';
import {
  BenefitChange,
  LifeEventChanges,
} from 'queries/useLifeEventChangesQuery/types';
import { selectPerPayPeriodText } from 'enrollment/services';

interface PendingChangesModalProps {
  id: string;
  index: number;
  benefitChanges: LifeEventChanges;
  firstName: string;
  modalOpen?: number | undefined;
  setModalOpen?: (value: number | undefined) => void;
}

export const PendingChangesModal = ({
  id,
  index,
  benefitChanges,
  firstName,
  modalOpen,
  setModalOpen,
}: PendingChangesModalProps) => {
  const { t } = useTranslation('pages.benefits');

  const perPayText = useSelector(selectPerPayPeriodText);

  const handleCloseModal = () => {
    setModalOpen?.(undefined);
  };

  const coverages = [benefitChanges.current, benefitChanges.pended];
  const styledPremium = (premium: string) => {
    return '$' + parseFloat(premium.replace(/[^0-9.]/g, '')).toFixed(2);
  };

  return (
    <Modal
      open={Boolean(modalOpen === index)}
      onClose={handleCloseModal}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
      data-testid={id}
      id={id}
    >
      <PendingChangesModalBodyWrapperStyled>
        <div className="d-flex justify-content-between mb-3">
          <h2>{t('pendingChanges.pendingTitle')}</h2>
          <ps-button
            id={`close-icon-${id}`}
            type="button"
            is-icon={true}
            icon="x"
            label="Close"
            level="tertiary"
            extra-class="blackIcon mr-3"
            onClick={handleCloseModal}
          />
        </div>
        <div className="row">
          {coverages.map((coverage: BenefitChange, index) => {
            const reqDependents = coverage?.dependents?.map((item: any) => {
              return item;
            });

            const dependentsNames =
              reqDependents?.map((dependent) => dependent.first_name) || [];
            const familyCovered = [firstName, ...dependentsNames].join(', ');

            return (
              <div className="col" key={index}>
                <div className="mb-4">
                  <p className="body1 semiBold">
                    {t(`pendingChanges.${index === 0 ? 'current' : 'pending'}`)}
                  </p>
                  <>
                    {coverage.level_name === 'Decline' ||
                    !coverage.subscriber_premium ? (
                      <>
                        <p className="headline-1 mb-3">
                          {t('pendingChanges.declined')}
                        </p>
                      </>
                    ) : (
                      <>
                        <p className="headline-1 mb-0">
                          {styledPremium(
                            index === 0
                              ? coverage.subscriber_premium || '0.00'
                              : coverage.req_subscriber_premium || '0.00'
                          )}
                        </p>
                        <p className="caption">
                          {coverage.subscriber_premium ? `${perPayText}` : ''}
                        </p>
                      </>
                    )}
                  </>
                </div>
                <div className="mb-4">
                  <p className="body1 semiBold">{t('startDate')}</p>
                  <p className="body1">
                    {String(
                      coverage.change_effective_date ||
                        benefitChanges.original_date
                    )}
                  </p>
                </div>
                <div className="mb-4">
                  <p className="body1 semiBold">{t('coverageLevel')}</p>
                  <Pill
                    text={coverage.level_name || t('pendingChanges.declined')}
                    color="#E6E6E6"
                  />
                </div>
                <div className="mb-4">
                  <p className="body1 semiBold">
                    {t('pendingChanges.familyCovered')}
                  </p>
                  <p className="body1">{familyCovered}</p>
                </div>
              </div>
            );
          })}
        </div>
        <>
          <p>{t('pendingChanges.text1')}</p>
          <p>{t('pendingChanges.text2')}</p>
        </>
      </PendingChangesModalBodyWrapperStyled>
    </Modal>
  );
};
